import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { IBackendCartEntries, ICartEntry } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { AvailabilityStatus } from 'components/availability-status';
import { Product } from 'components/product';
import { ItemAvailabilityStatus } from 'enums/menu';
import { ICartEntryFragment } from 'generated/rbi-graphql';
import useAddToCartRecentItem from 'hooks/use-add-to-cart-recent-item';
import {
  FormatRecentItemsCaloriesArgs,
  useRecentItemsCalories,
} from 'hooks/use-recent-items-calories/use-recent-items-calories';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ItemAvailability } from 'state/menu-options';
import { useOrderContext } from 'state/order';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';
import { getMenuItemDayParts } from 'utils/availability';
import { getSelectionsListFromEntry } from 'utils/cart';

import { StyledRecentItem, StyledRecentItemButton, StyledRecentItemProduct } from './styled';

interface RecentItemsCarouselItemProps {
  cartEntry: IBackendCartEntries | ICartEntryFragment | null;
  onItemAdded: () => void;
}

const RecentItemsCarouselItem: React.FC<RecentItemsCarouselItemProps> = ({
  cartEntry,
  onItemAdded,
}) => {
  const { formatMessage } = useIntl();
  const [frontendEntry, setFrontendEntry] = useState<ICartEntry | null>(null);
  const [itemAvailability, setItemAvailability] = useState<ItemAvailability>({
    availabilityStatus: ItemAvailabilityStatus.UNAVAILABLE,
    data: undefined,
  });
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const { addToCart, SwitchCartMethodDialog } = useAddToCartRecentItem({
    cartEntry: frontendEntry,
  });
  const mapToFrontendEntry = useMapToFrontendCartEntry();
  const orderContext = useOrderContext();

  const enableRecentItemsWithModifiers = useFlag(
    LaunchDarklyFlag.ENABLE_RECENT_ITEMS_WITH_MODIFIERS
  );
  const enableDisplayCalories = useFlag(LaunchDarklyFlag.ENABLE_FAVORITES_DISPLAY_CALORIES);
  const formatRecentItemsCalories = useRecentItemsCalories();

  useEffect(() => {
    const handleEffect = async () => {
      setCheckingAvailability(true);
      const { cartEntry: mappedEntry, status, data } = await mapToFrontendEntry(
        cartEntry as IBackendCartEntries,
        orderContext?.cartEntries
      );
      setItemAvailability({ availabilityStatus: status, data: data ?? undefined });
      setFrontendEntry(mappedEntry);
      setCheckingAvailability(false);
    };
    handleEffect();
    //eslint-disable-next-line
  }, [cartEntry, mapToFrontendEntry, JSON.stringify(orderContext?.cartEntries)]);

  if (!cartEntry || itemAvailability?.availabilityStatus === ItemAvailabilityStatus.OUT_OF_MENU) {
    return null;
  }

  const itemSelections =
    enableRecentItemsWithModifiers && frontendEntry?.children
      ? getSelectionsListFromEntry(frontendEntry)
      : [];

  const dayParts = itemAvailability.data ? getMenuItemDayParts(itemAvailability.data) : [];

  const isAvailable = [
    ItemAvailabilityStatus.AVAILABLE,
    ItemAvailabilityStatus.STORE_NOT_SELECTED,
  ].includes(itemAvailability.availabilityStatus);

  const loading = !frontendEntry;

  const handleAddToCart = () => {
    addToCart();
    onItemAdded();
  };

  const calories = enableDisplayCalories
    ? formatRecentItemsCalories({
        menuItem: itemAvailability.data as FormatRecentItemsCaloriesArgs['menuItem'],
        frontendEntry,
        quantity: cartEntry.quantity,
      })
    : undefined;

  return (
    <>
      <StyledRecentItem>
        <StyledRecentItemProduct>
          <Product
            expandable={false}
            loading={loading}
            image={cartEntry.image ?? ''}
            imageSize="large"
            name={frontendEntry?.name}
            selections={itemSelections}
            calories={calories}
          />
        </StyledRecentItemProduct>
        {!loading && (
          <StyledRecentItemButton>
            {!isAvailable ? (
              <AvailabilityStatus
                availabilityStatus={itemAvailability.availabilityStatus}
                dayParts={dayParts}
                hideWhenAvailable
              />
            ) : (
              <ActionButton
                disabled={!isAvailable || checkingAvailability}
                size={ActionButtonSizes.SMALL}
                variant={ActionButtonVariants.OUTLINE}
                onClick={handleAddToCart}
                data-testid="recent-item-add-btn"
                aria-label={formatMessage(
                  { id: 'addItemToCart' },
                  { name: frontendEntry?.name || '' }
                )}
              >
                {`+ ${formatMessage({ id: 'addToCart' })}`}
              </ActionButton>
            )}
          </StyledRecentItemButton>
        )}
      </StyledRecentItem>
      <SwitchCartMethodDialog />
    </>
  );
};

export default RecentItemsCarouselItem;
